import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifications, type NotificationDto, type NotificationsInbox } from '@/api/endpoints';
import { Icon } from '@/components/Icon';

/** Format „12 min temu", „2 godz temu", „3 dni temu" lub pełna data jeśli >7d. */
function fmtRelative(iso: string): string {
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  if (Number.isNaN(ms) || ms < 0) return iso;
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'teraz';
  if (min < 60) return `${min} min temu`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} godz. temu`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days} ${days === 1 ? 'dzień' : 'dni'} temu`;
  return d.toLocaleDateString('pl-PL');
}

const CATEGORY_COLORS: Record<string, string> = {
  OVERDUE_DOCUMENTS: 'border-l-red-500',
  STUDY_DEADLINE: 'border-l-amber-500',
  STUDY_REJECTION: 'border-l-red-600',
  WORKFLOW_STEP_ASSIGNED: 'border-l-water-500',
  AI_DRAFT_READY: 'border-l-purple-500',
  WEEKLY_SUMMARY: 'border-l-ink-400',
  INVOICE_RECEIVED: 'border-l-green-500',
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [inbox, setInbox] = useState<NotificationsInbox | null>(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Polling co 60s w tle — żeby badge unread się aktualizował bez refresh strony.
  useEffect(() => {
    let mounted = true;
    const fetchInbox = () => {
      notifications.inbox(20)
        .then(d => { if (mounted) setInbox(d); })
        .catch(() => { /* nonfatal — sieć padła, zostawiamy poprzedni stan */ });
    };
    fetchInbox();
    const t = setInterval(fetchInbox, 60_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  // Klik poza dropdown — zamknij
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const refresh = async () => {
    setLoading(true);
    try { setInbox(await notifications.inbox(20)); }
    finally { setLoading(false); }
  };

  const onItemClick = async (n: NotificationDto) => {
    setOpen(false);
    if (!n.readAt) {
      try { await notifications.markRead(n.id); } catch { /* nonfatal */ }
    }
    if (n.link) {
      // Tylko relatywne linki — nie otwieramy zewnętrznych z bell-a (XSS risk dla starych powiadomień).
      if (n.link.startsWith('/')) navigate(n.link);
    }
    refresh();
  };

  const onMarkAllRead = async () => {
    try {
      await notifications.markAllRead();
      refresh();
    } catch { /* nonfatal */ }
  };

  const onRemove = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await notifications.remove(id);
      setInbox(prev => prev
        ? { unreadCount: prev.unreadCount, notifications: prev.notifications.filter(x => x.id !== id) }
        : null);
    } catch { /* nonfatal */ }
  };

  const unread = inbox?.unreadCount ?? 0;

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => { setOpen(o => !o); if (!open) refresh(); }}
        className="btn btn-ghost btn-icon relative"
        title={unread > 0 ? `${unread} nieprzeczytanych powiadomień` : 'Powiadomienia'}
      >
        <Icon name="bell"/>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full
                           bg-red-500 text-white text-[9px] font-bold leading-[16px]
                           flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-[420px] max-h-[600px] overflow-hidden
                        bg-white border border-ink-100 rounded-md shadow-lg z-30 flex flex-col">
          <div className="px-3 py-2 border-b border-ink-100 flex items-center gap-2">
            <span className="text-[12px] font-semibold text-ink-700">Powiadomienia</span>
            {unread > 0 && (
              <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                {unread} nowych
              </span>
            )}
            <button
              onClick={onMarkAllRead}
              disabled={unread === 0 || loading}
              className="ml-auto text-[11px] text-water-700 hover:underline disabled:opacity-40 disabled:no-underline"
            >Oznacz wszystkie jako przeczytane</button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {!inbox && <div className="p-4 text-ink-400 text-[12px]">Ładowanie…</div>}
            {inbox && inbox.notifications.length === 0 && (
              <div className="p-6 text-center text-[12px] text-ink-400">
                <Icon name="bell" className="w-8 h-8 mx-auto mb-2 text-ink-300"/>
                Brak powiadomień. Cron-jobs i workflow events trafiają tutaj — ostatnie 30 dni.
              </div>
            )}
            {inbox && inbox.notifications.map(n => {
              const colorClass = CATEGORY_COLORS[n.category] ?? 'border-l-ink-300';
              const unreadCls = !n.readAt ? 'bg-water-50/40' : '';
              return (
                <button
                  key={n.id}
                  onClick={() => onItemClick(n)}
                  className={`w-full text-left p-3 border-b border-ink-100 last:border-b-0
                             border-l-[3px] ${colorClass} ${unreadCls} hover:bg-ink-50 transition group`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className={'text-[13px] ' + (!n.readAt ? 'font-semibold text-ink-900' : 'text-ink-700')}>
                        {n.title}
                      </div>
                      {n.body && (
                        <div className="text-[11px] text-ink-600 mt-0.5 line-clamp-2 whitespace-pre-wrap">
                          {n.body}
                        </div>
                      )}
                      <div className="text-[10px] text-ink-400 mt-1 font-mono">
                        {fmtRelative(n.createdAt)}
                      </div>
                    </div>
                    <button
                      onClick={(e) => onRemove(n.id, e)}
                      className="text-ink-300 hover:text-red-600 p-1 opacity-0 group-hover:opacity-100 transition"
                      title="Usuń"
                    >
                      <Icon name="x" className="w-3 h-3"/>
                    </button>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="px-3 py-1.5 border-t border-ink-100 text-center">
            <button
              onClick={() => { setOpen(false); navigate('/settings'); }}
              className="text-[11px] text-ink-500 hover:text-ink-800"
            >
              Ustawienia powiadomień →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
